import { useEffect, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'

import TerminologyCard from '../cards/TerminologyCard'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Loading from '../loading/Loading'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress' 
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import ReorderIcon from '@mui/icons-material/Reorder'
import AppsIcon from '@mui/icons-material/Apps'
import MenuItem from '@mui/material/MenuItem'

import { initializeTerminology, searchTerminology } from '../../reducers/terminologyReducer'
import { initializeTags } from '../../reducers/tagsReducers'
import { setErrorNotification } from '../../reducers/notificationReducer'
import Pagination from '@mui/material/Pagination'

import { buildQuery, buildSearchQuery } from '../../services/utils.js/query'
import { PAGES } from '../navigation/routes'
import TerminologyList from '../lists/TerminologyList'
import { SearchStyle } from '../styles'
import TagSelector from '../tags/TagSelector'
import TerminologyViewModal from '../modals/TerminologyViewModal'
import ExportGlossaryButton from '../shared/ExportGlossaryButton'
import terminologyService from '../../services/terminology'
import { formatLastUpdated } from '../../services/utils.js/date'

// Card view is disabled; flip to true to restore the list/card toggle.
const CARD_VIEW_ENABLED = false

const Terminology = () => {
    const { id: openResourceId } = useParams()
    const [searchParams] = useSearchParams()
    const [list, setList] = useState(true)
    const showList = list || !CARD_VIEW_ENABLED
    const navigate = useNavigate()
    const [resourcesToShow, setResourcesToShow] = useState(null)
    const [sortRes, setSortRes] = useState('term')
    const [term, setTerm] = useState('')
    const [formTags, setFormTags] = useState([])
    const [linkedResource, setLinkedResource] = useState(null)
    const [lastUpdated, setLastUpdated] = useState(null)

    const user = useSelector(state => state.user)
    const terminologyData = useSelector(state => state.terminology)
    const terminology = useMemo(() => terminologyData?.results || [], [terminologyData?.results])
    const totalPages = terminologyData?.total_pages || 1
    const isLoading = terminologyData?.loading || false
    const dispatch = useDispatch()

    const [page, setPage] = useState(1)

    useEffect(() => {
        if(searchParams.toString()) {
            let formTagsValue = searchParams.get('tags')
            let termValue = searchParams.get('title')  // Fixed: reading 'title' parameter from URL
            let orderingValue = searchParams.get('ordering')
            let pageValue = parseInt(searchParams.get('page')) || 1

            if(formTagsValue) setFormTags(formTagsValue.split(',').map(tag => { return {label: tag, value: tag} }))
            if(termValue) setTerm(termValue)
            if(orderingValue) setSortRes(orderingValue)
            setPage(pageValue)

            const query = buildSearchQuery(null, null, termValue, formTagsValue, orderingValue)

            dispatch(searchTerminology(query, pageValue))
        } else {
            const pageValue = parseInt(searchParams.get('page')) || 1
            setPage(pageValue)
            dispatch(initializeTerminology(pageValue))
        }
        dispatch(initializeTags())
    }, [searchParams, user, dispatch])

    useEffect(() => {
        let isActive = true

        if (openResourceId) {
            terminologyService.retrieveSingle(openResourceId)
                .then(data => {
                    if (!isActive) return
                    setLinkedResource(data)
                })
                .catch(() => {
                    if (!isActive) return
                    dispatch(setErrorNotification('Term not found'))
                    navigate(PAGES.terminology.path, { replace: true })
                })
        } else {
            setLinkedResource(null)
        }

        return () => {
            isActive = false
        }
    }, [openResourceId, dispatch, navigate])

    useEffect(() => {
        let isActive = true
        // Subtitle is non-critical; if it fails to load we simply omit it.
        terminologyService.getLastUpdated()
            .then(value => { if (isActive) setLastUpdated(value) })
            .catch(() => { if (isActive) setLastUpdated(null) })
        return () => { isActive = false }
    }, [])

    useEffect(() => {
        if(terminology && terminology.length > 0) {
            if (user && (user.staff || user.terminology_admin)) {
                setResourcesToShow(terminology.filter(term => term.approved))
            } else {
                setResourcesToShow(terminology)
            }
        } else if (!isLoading) {
            setResourcesToShow([])
        }
    }, [terminology, user, isLoading])

    const handleSort = (event) => {
        const orderingValue = event.target.value
        setSortRes(orderingValue)
        const currentParams = new URLSearchParams(searchParams.toString())
        currentParams.set('ordering', orderingValue)
        currentParams.set('page', '1')
        navigate(`${PAGES.terminology.path}?${currentParams.toString()}`)
    }

    const handleSubmit = (event) => {
        event.preventDefault()

        let formTagsValue = null
        let termValue = null
        if(formTags && formTags.length > 0) formTagsValue = formTags.map(tag => tag.value)
        if(term) termValue = term

        const orderingValue = searchParams.get('ordering')
        const query = buildQuery(null, null, termValue, formTagsValue, orderingValue)
        const currentQuery = buildSearchQuery(null, null, searchParams.get('title'), searchParams.get('tags'), orderingValue)

        if(query !== currentQuery) {
            navigate(`${PAGES.terminology.path}?${query}`)
        }
    }

    const handlePageChange = (_, value) => {
        const currentParams = new URLSearchParams(searchParams.toString())
        currentParams.set('page', value.toString())
        navigate(`${PAGES.terminology.path}?${currentParams.toString()}`)
    }

    return (
        <Container sx={{...SearchStyle.container, marginTop: '100px'}}>
            <Typography {...SearchStyle.title} gutterBottom>
                Living Glossary
            </Typography>
            {lastUpdated && (
                <Typography {...SearchStyle.subtitle} gutterBottom>
                    Last updated: {formatLastUpdated(lastUpdated)}
                </Typography>
            )}
            <Typography {...SearchStyle.paragraph} gutterBottom>
                This living glossary is designed as a practical and accessible resource for anyone engaging with AI in medicine, from clinicians and researchers to policymakers and students. Each term includes a clear definition and a relevant application to medicine, helping to bridge the gap between technical AI concepts and their real-world impact in healthcare. Use this glossary to improve interdisciplinary communication, enhance your understanding of AI’s role in medicine, and ensure clarity in discussions, research, and education. In the future, our goal is to create a multi-format living glossary that will be regularly updated with new terms and phrases as the field of AI health evolves.
            </Typography>
            <Typography {...SearchStyle.paragraph} gutterBottom>
                The living glossary needs your help to grow and adapt as the field of AI in medicine advances. We welcome suggestions for new terms to add, as well as edits to existing terms. If you have a term you would like to see added, please submit a suggestion through the button below. If you have a suggestion for a change to an existing term, click the "Suggest Changes" button on that term. Your contributions will help ensure that this glossary remains a relevant and valuable resource for the entire AI in medicine community.
            </Typography>
            <Box sx={{...SearchStyle.suggestButtonContainer}}>
                <Button
                    variant="contained"
                    sx={{...SearchStyle.suggestButton}}
                    disabled={!(user && !user.anon)}
                    onClick={() => navigate(PAGES.suggest_term.path)}
                >
                    {user && !user.anon ? 'Suggest a Term' : 'Log in to suggest a term'}
                </Button>
            </Box>
            <Box sx={{...SearchStyle.exportButtonContainer}}>
                <ExportGlossaryButton buttonProps={{ variant: 'outlined' }} sx={SearchStyle.exportButton} />
            </Box>
            <form style={SearchStyle.form} onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            sx={{width: '100%'}}
                            variant='outlined'
                            label='Search Term'
                            value={term}
                            onChange={(e) => setTerm(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TagSelector
                            tagType='default'
                            selectedTags={formTags}
                            onChange={setFormTags}
                            multiple={true}
                            label='Filter by Key Words'
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={SearchStyle.filtersContainer}>
                    <Grid item xs={12} md={6}>
                        <Button
                            sx={SearchStyle.searchButton}
                            fullWidth
                            variant="contained"
                            disabled={isLoading}
                            type='submit'
                        >
                        {isLoading ?
                            <CircularProgress
                            size={24}
                            sx={{
                                color: '#FFF',
                            }}
                            /> : <>Search</>
                        }
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end', width: '100%' }}>
                            <TextField
                                    value={sortRes}
                                    select
                                    label="Sort By"
                                    InputLabelProps={{ shrink: true }}
                                    onChange={handleSort}
                                    sx={{ width: '100%' }}
                                >
                                <MenuItem value={'term'}>Term A-Z</MenuItem>
                                <MenuItem value={'-term'}>Term Z-A</MenuItem>
                            </TextField>
                            {CARD_VIEW_ENABLED && <Box sx={SearchStyle.viewContainer}>
                                <IconButton sx={{ color: list? '#002A5C' : '#CECFCB'}} onClick={() => setList(true)}>
                                    <ReorderIcon />
                                </IconButton>
                                <IconButton sx={{ color: !list? '#002A5C' : '#CECFCB'}} onClick={() => setList(false)}>
                                    <AppsIcon />
                                </IconButton>
                            </Box>}
                        </Box>
                    </Grid>
                </Grid>
            </form>
            {linkedResource && (
                <TerminologyViewModal
                    {...linkedResource}
                    initialOpen={true}
                    onExternalClose={() => navigate(PAGES.terminology.path, { replace: true })}
                />
            )}
            {isLoading && <Loading />}
            {!isLoading && ((resourcesToShow && resourcesToShow.length > 0)?
                <>
                    <Grid container spacing={2}>
                        {showList? resourcesToShow.map(terminology =>
                            <Grid key={terminology.id} item xs={12} >
                                <TerminologyList {...terminology}/>
                            </Grid>
                        ) : resourcesToShow.map(terminology =>
                            <Grid key={terminology.id} item xs={12} md={4} sx={SearchStyle.card}>
                                <TerminologyCard {...terminology}/>
                            </Grid>
                        )}
                    </Grid>
                    <Pagination sx={SearchStyle.pagination} count={totalPages} page={page} onChange={handlePageChange} />
                </> :
                <Typography {...SearchStyle.noResources.props} gutterBottom sx={SearchStyle.noResources.style}>
                    No Terms Found
                </Typography>)
            }
            <Box sx={{textAlign: 'center', padding: '10px'}}>
                <Typography variant='overline' component='p'>
                    All resources provided are strictly owned and distributed by the creators indicated on the resource
                </Typography>
            </Box>
        </Container>
    )
}

export default Terminology
